import axios from 'axios';
import { message } from 'antd';

// Token 管理工具
const TokenManager = {
  // 获取当前 token（优先从 sessionStorage，回退到 localStorage）
  getToken: () => {
    return sessionStorage.getItem('auth_token') || localStorage.getItem('auth_token');
  },

  // 设置 token（同时设置到 sessionStorage 和 localStorage，实现多标签同步）
  setToken: (token) => {
    if (!token) {
      sessionStorage.removeItem('auth_token');
      localStorage.removeItem('auth_token');
      return;
    }
    sessionStorage.setItem('auth_token', token);
    localStorage.setItem('auth_token', token);
  },

  // 清除 token
  clearToken: () => {
    sessionStorage.removeItem('auth_token');
    localStorage.removeItem('auth_token');
  },
};

// 是否正在刷新 token
let isRefreshing = false;
// 等待刷新的请求队列
let refreshSubscribers = [];

// 添加等待刷新的请求
const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

// 通知所有等待的请求
const onRefreshed = (token) => {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
};

// 创建axios实例
const api = axios.create({
  // 默认使用相对路径，开发环境依赖 CRA proxy，生产依赖 Nginx 代理
  baseURL: process.env.REACT_APP_API_BASE_URL || '/',
  timeout: 10000,
  withCredentials: false,  // 不携带 Cookie，纯 JWT Header 认证
  headers: {
    'Content-Type': 'application/json',
  },
});

const stableStringify = (value) => {
  if (value === null || value === undefined) return String(value);
  const t = typeof value;
  if (t === 'string') return JSON.stringify(value);
  if (t === 'number' || t === 'boolean') return String(value);
  if (value instanceof Date) return JSON.stringify(value.toISOString());
  if (typeof URLSearchParams !== 'undefined' && value instanceof URLSearchParams) {
    return JSON.stringify(value.toString());
  }
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  if (t === 'object') {
    const keys = Object.keys(value).sort();
    return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(value[k])}`).join(',')}}`;
  }
  return JSON.stringify(String(value));
};

const buildGetDedupKey = (url, config) => {
  const baseURL = config?.baseURL ?? api.defaults.baseURL ?? '';
  const params = config?.params ?? null;
  const responseType = config?.responseType ?? '';
  const headers = config?.headers ?? null;
  return ['GET', baseURL, url, responseType, stableStringify(params), stableStringify(headers)].join('|');
};

const inFlightGetRequests = new Map();
const originalGet = api.get.bind(api);
api.get = (url, config = {}) => {
  if (config?.dedup === false) return originalGet(url, config);
  const key = buildGetDedupKey(url, config);
  const existing = inFlightGetRequests.get(key);
  if (existing) return existing;
  const promise = originalGet(url, config).finally(() => {
    inFlightGetRequests.delete(key);
  });
  inFlightGetRequests.set(key, promise);
  return promise;
};

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    // 公开接口列表：不携带 token，避免后端返回 401
    const publicPaths = ['/api/v1/captcha', '/api/v1/auth/login', '/api/v1/auth/register', '/api/v1/auth/send-code', '/api/v1/auth/reset-password',
      // 公开查验（fix-verify-public）：匿名语义端点。携带登录态会使后端按 userId
      // 收窄查询范围，扫码核验他人存证会被误判"不存在"；与静态落地页 fetch 行为对齐
      '/api/v1/evidence/verify/result'];
    const isPublicPath = publicPaths.some(path => config.url?.startsWith(path));

    if (!isPublicPath) {
      // 从 TokenManager 获取 token 并添加到 Authorization header
      const token = TokenManager.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Token 刷新函数（通过 user 后端代理）
// 注意：使用 _retry 标记避免无限递归
const refreshToken = async () => {
  // 获取当前 token
  const currentToken = TokenManager.getToken();
  if (!currentToken) {
    throw new Error('无有效 token 可刷新');
  }

  try {
    // 使用 _retry 标记，让响应拦截器知道这是刷新 token 的请求
    // 这样即使返回 401 也不会再次尝试刷新
    // 代理路径：/api/v1/auth/refresh-token
    const response = await api.post('/api/v1/auth/refresh-token', null, {
      _retry: true  // 标记此请求，防止响应拦截器重复刷新
    });

    // 响应拦截器已经将 {code: 200, data: {...}} 转换为 {data, message}
    // 所以 response.data 就是新的 token 数据
    if (response.data) {
      const newToken = response.data.token || response.data;
      TokenManager.setToken(newToken);
      return newToken;
    }
    throw new Error('刷新 token 失败：返回数据无效');
  } catch (error) {
    // 刷新失败，清除 token 并跳转登录
    TokenManager.clearToken();
    const isAuthPage = window.location.pathname.startsWith('/auth');
    if (!isAuthPage) {
      window.location.href = '/auth/login';
    }
    throw error;
  }
};

// 响应拦截器
api.interceptors.response.use(
  (response) => {
    if (response && response.config && response.config.responseType === 'blob') {
      return response;
    }
    // 统一处理响应数据
    const { code, message: msg, data } = response.data;

    if (code === 200) {
      return { data, message: msg };
    } else {
      // 业务错误
      const err = new Error(msg || '请求失败');
      err.code = code;
      err.data = data;
      return Promise.reject(err);
    }
  },
  async (error) => {
    // HTTP错误处理
    if (error.response) {
      const { status, data, config } = error.response;
      const responseMessage = (data && (data.message || data.msg)) ? String(data.message || data.msg) : '';

      // Token 过期处理：尝试刷新 token（仅在非认证页面且有 token 时）
      const isAuthPage = window.location.pathname.startsWith('/auth');
      const hasToken = TokenManager.getToken();

      if (status === 401 && config && !config._retry && !isAuthPage && hasToken) {
        config._retry = true;

        // 如果正在刷新，将请求加入队列
        if (isRefreshing) {
          return new Promise((resolve) => {
            subscribeTokenRefresh((token) => {
              config.headers.Authorization = `Bearer ${token}`;
              resolve(api(config));
            });
          }).catch((err) => Promise.reject(err));
        }

        // 开始刷新 token
        isRefreshing = true;
        try {
          const newToken = await refreshToken();
          isRefreshing = false;
          onRefreshed(newToken);
          // 重试原请求
          config.headers.Authorization = `Bearer ${newToken}`;
          return api(config);
        } catch (refreshError) {
          isRefreshing = false;
          return Promise.reject(refreshError);
        }
      }

      switch (status) {
        case 401:
          // 未授权：清除持久化状态并跳转到登录页
          try {
            localStorage.removeItem('persist:root');
          } catch (e) {
          }
          TokenManager.clearToken();
          const isAuthPage = window.location.pathname.startsWith('/auth');
          if (!isAuthPage) {
            window.location.href = '/auth/login';
            message.error('登录已过期，请重新登录');
            return new Promise(() => {});
          }
          error.message = responseMessage || '登录已过期，请重新登录';
          break;
        case 403:
          error.message = responseMessage || '权限不足';
          break;
        case 404:
          error.message = responseMessage || '请求的资源不存在';
          break;
        case 500:
          error.message = responseMessage || '服务器内部错误';
          break;
        default:
          error.message = responseMessage || '网络错误';
      }
    } else if (error.request) {
      // 网络错误
      error.message = '网络连接失败，请检查网络设置';
    } else {
      // 其他错误
      error.message = error.message || '请求失败';
    }

    return Promise.reject(error);
  }
);

export default api;
export { TokenManager };
